import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Badge } from '../ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import { Separator } from '../ui/separator'
import { ColorPicker } from '../ui/color-picker'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../ui/breadcrumb'
import {
  Palette,
  Save,
  Loader2,
  Trash2,
  Plus,
  User,
  Type,
  QrCode,
  Image as ImageIcon,
  Square,
  Minus,
  ArrowLeft,
  GripVertical,
  Eye,
  Circle,
  Pentagon,
  Upload,
  PenTool,
  Undo2,
  Redo2,
  RotateCcw,
  Magnet,
} from 'lucide-react'
import { Checkbox } from '../ui/checkbox'
import BadgeTemplateEndpoints, {
  BadgeTemplate,
  BadgeTemplateElement,
  BadgeTemplateLayoutDefinition,
  AvailableField,
  ElementOverride,
  PathShape,
  PathPoint,
} from '../../endpoints/BadgeTemplateEndpoints'
import { BadgePrintingLogic } from '../../common/BadgePrintingLogic'
import { useApplicationStore } from '../../stores/useApplicationStore'
import { toast } from 'sonner'
import { extractErrorMessage } from '@/common/extractErrorMessage'
import { cn } from '@/lib/utils'

// The editing canvas zooms to a comfortable size regardless of the badge's
// physical dimensions, so a small CR80 card is just as easy to edit as a large
// credential. `scale` (pixels per mm) is derived per-render from the badge size
// to fit within this pixel box, clamped so tiny/huge cards stay usable.
const CANVAS_FIT_W = 460
const CANVAS_FIT_H = 620
const MIN_SCALE = 1.5
const MAX_SCALE = 8

const computeCanvasScale = (widthMm: number, heightMm: number): number => {
  if (!widthMm || !heightMm) return 2
  const fit = Math.min(CANVAS_FIT_W / widthMm, CANVAS_FIT_H / heightMm)
  return Math.max(MIN_SCALE, Math.min(MAX_SCALE, fit))
}

const DEFAULT_ELEMENTS: BadgeTemplateElement[] = []

// Physical card sizes in mm, expressed as [long edge, short edge].
// CR80 (ISO/IEC 7810 ID-1) is the standard badge/credential card and prints
// perfectly on card printers with no scaling. Dimensions are stored on the
// template in mm and the backend renders the PDF page at exactly this size.
const CARD_SIZES: {
  value: string
  label: string
  long: number
  short: number
}[] = [
  { value: 'CR80', label: 'CR80 — Standard ID card (85.6 × 53.98 mm)', long: 85.6, short: 53.98 },
  { value: 'CR79', label: 'CR79 (83.9 × 51 mm)', long: 83.9, short: 51 },
  { value: 'A7', label: 'A7 (105 × 74 mm)', long: 105, short: 74 },
  { value: 'A6', label: 'A6 (148 × 105 mm)', long: 148, short: 105 },
  { value: 'LARGE', label: 'Large credential (210 × 325 mm)', long: 325, short: 210 },
  { value: 'CUSTOM', label: 'Custom', long: 0, short: 0 },
]

// Default new templates to a CR80 card so badges print at the correct size.
const DEFAULT_CARD = CARD_SIZES[0]

/** Returns [width, height] in mm for a card size at the given orientation. */
const dimsForOrientation = (
  card: { long: number; short: number },
  orientation: string
): [number, number] =>
  orientation === 'LANDSCAPE'
    ? [card.long, card.short]
    : [card.short, card.long]

/** Best-match card size for existing width/height (used when editing). */
const matchCardSize = (width: number, height: number): string => {
  const long = Math.max(width, height)
  const short = Math.min(width, height)
  const found = CARD_SIZES.find(
    (c) =>
      c.value !== 'CUSTOM' &&
      Math.abs(c.long - long) < 0.5 &&
      Math.abs(c.short - short) < 0.5
  )
  return found ? found.value : 'CUSTOM'
}

const ELEMENT_PRESETS: {
  type: BadgeTemplateElement['type']
  label: string
  icon: any
  defaults: Partial<BadgeTemplateElement>
}[] = [
  {
    type: 'PHOTO',
    label: 'Photo',
    icon: User,
    defaults: { width: 80, height: 100 },
  },
  {
    type: 'TEXT',
    label: 'Text Field',
    icon: Type,
    defaults: {
      width: 150,
      height: 20,
      field: 'firstName',
      fontSize: 14,
      fontWeight: 'BOLD',
      color: '#000000',
      align: 'CENTER',
    },
  },
  {
    type: 'BARCODE',
    label: 'Barcode',
    icon: QrCode,
    defaults: { width: 44, height: 44 },
  },
  {
    type: 'LOGO',
    label: 'Logo',
    icon: ImageIcon,
    defaults: { width: 60, height: 20 },
  },
  {
    type: 'RECT',
    label: 'Rectangle',
    icon: Square,
    defaults: { width: 210, height: 30, color: '#DE7027' },
  },
  {
    type: 'ELLIPSE',
    label: 'Ellipse',
    icon: Circle,
    defaults: { width: 60, height: 60, color: '#DE7027' },
  },
  {
    type: 'LINE',
    label: 'Line',
    icon: Minus,
    defaults: { width: 190, height: 2, color: '#cccccc', lineWidth: 1 },
  },
  {
    type: 'PATH',
    label: 'Shape',
    icon: Pentagon,
    defaults: { width: 60, height: 60, color: '#DE7027', shape: 'STAR' },
  },
  {
    type: 'IMAGE',
    label: 'Custom Image',
    icon: Upload,
    defaults: { width: 80, height: 80 },
  },
  {
    type: 'CUSTOM_PATH',
    label: 'Custom Shape',
    icon: PenTool,
    defaults: {
      width: 0,
      height: 0,
      color: '#DE7027',
      closed: true,
      points: [
        { x: 60, y: 40 },
        { x: 100, y: 120 },
        { x: 20, y: 120 },
      ],
    },
  },
]

// SVG path data matching the backend's normalized shape definitions (0-1 coords, y=0 is top)
const SHAPE_SVG_PATHS: Record<PathShape, string> = {
  STAR: 'M 0.5,0 L 0.618,0.382 L 1,0.382 L 0.691,0.618 L 0.809,1 L 0.5,0.764 L 0.191,1 L 0.309,0.618 L 0,0.382 L 0.382,0.382 Z',
  HEXAGON: 'M 0.5,0 L 1,0.25 L 1,0.75 L 0.5,1 L 0,0.75 L 0,0.25 Z',
  SHIELD:
    'M 0.5,0 L 1,0.1 L 1,0.55 C 1,0.8 0.75,1 0.5,1 C 0.25,1 0,0.8 0,0.55 L 0,0.1 Z',
  DIAMOND: 'M 0.5,0 L 1,0.5 L 0.5,1 L 0,0.5 Z',
  HEART:
    'M 0.5,0.92 C 0.35,0.82 0,0.58 0,0.3 C 0,0.1 0.15,0 0.3,0 C 0.4,0 0.48,0.06 0.5,0.15 C 0.52,0.06 0.6,0 0.7,0 C 0.85,0 1,0.1 1,0.3 C 1,0.58 0.65,0.82 0.5,0.92 Z',
  ARROW: 'M 0,0.25 L 0.6,0.25 L 0.6,0 L 1,0.5 L 0.6,1 L 0.6,0.75 L 0,0.75 Z',
  RIBBON:
    'M 0.05,0 L 0.95,0 L 0.95,0.75 L 1,0.75 L 0.82,1 L 0.65,0.75 L 0.35,0.75 L 0.18,1 L 0,0.75 L 0.05,0.75 Z',
}

const PATH_SHAPES: { value: PathShape; label: string }[] = [
  { value: 'STAR', label: 'Star' },
  { value: 'HEXAGON', label: 'Hexagon' },
  { value: 'SHIELD', label: 'Shield' },
  { value: 'DIAMOND', label: 'Diamond' },
  { value: 'HEART', label: 'Heart' },
  { value: 'ARROW', label: 'Arrow' },
  { value: 'RIBBON', label: 'Ribbon' },
]

// Properties that can be marked as overridable per element type
const OVERRIDABLE_PROPERTIES: Record<
  string,
  { property: string; label: string }[]
> = {
  TEXT: [
    { property: 'fontSize', label: 'Font Size' },
    { property: 'fontWeight', label: 'Font Weight' },
    { property: 'color', label: 'Text Color' },
    { property: 'backgroundColor', label: 'Background Color' },
    { property: 'align', label: 'Alignment' },
  ],
  RECT: [
    { property: 'color', label: 'Color' },
    { property: 'borderRadius', label: 'Border Radius' },
    { property: 'opacity', label: 'Opacity' },
  ],
  ELLIPSE: [
    { property: 'color', label: 'Color' },
    { property: 'opacity', label: 'Opacity' },
  ],
  PATH: [
    { property: 'color', label: 'Color' },
    { property: 'opacity', label: 'Opacity' },
  ],
  LINE: [{ property: 'color', label: 'Color' }],
  CUSTOM_PATH: [
    { property: 'color', label: 'Fill Color' },
    { property: 'opacity', label: 'Opacity' },
  ],
}

/** Builds an SVG path `d` string from PathPoints in absolute mm, scaled to canvas pixels. */
function buildCustomPathD(
  points: PathPoint[],
  closed: boolean,
  scale: number = 1
): string {
  if (!points || points.length < 2) return ''
  const s = scale
  let d = `M ${points[0].x * s},${points[0].y * s}`
  for (let i = 1; i < points.length; i++) {
    const pt = points[i]
    if (pt.curve) {
      d += ` C ${(pt.cx1 ?? 0) * s},${(pt.cy1 ?? 0) * s} ${(pt.cx2 ?? 0) * s},${(pt.cy2 ?? 0) * s} ${pt.x * s},${pt.y * s}`
    } else {
      d += ` L ${pt.x * s},${pt.y * s}`
    }
  }
  if (closed) d += ' Z'
  return d
}

function generateElementId(): string {
  return `el_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`
}

export const BadgeBuilder: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>()
  const navigate = useNavigate()
  const isEditing = templateId && templateId !== 'new'

  const [loading, setLoading] = useState(!!isEditing)
  const [saving, setSaving] = useState(false)
  const [name, setName] = useState('New Badge Template')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('CUSTOM')
  const [orientation, setOrientation] = useState('PORTRAIT')
  const [cardSize, setCardSize] = useState(DEFAULT_CARD.value)
  const [badgeWidth, setBadgeWidth] = useState(
    dimsForOrientation(DEFAULT_CARD, 'PORTRAIT')[0]
  )
  const [badgeHeight, setBadgeHeight] = useState(
    dimsForOrientation(DEFAULT_CARD, 'PORTRAIT')[1]
  )
  // Display zoom (px per mm). Keeps the canvas a workable size for any card.
  const scale = computeCanvasScale(badgeWidth, badgeHeight)
  const [backgroundColor, setBackgroundColor] = useState('#ffffff')
  const [elements, setElements] = useState<BadgeTemplateElement[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [availableFields, setAvailableFields] = useState<AvailableField[]>([])
  const [isGlobal, setIsGlobal] = useState(true)
  const [selectedClientIds, setSelectedClientIds] = useState<number[]>([])
  const [allClients, setAllClients] = useState<
    Array<{ id: number; name: string }>
  >([])

  // History for undo/redo
  const historyRef = useRef<{
    past: BadgeTemplateElement[][]
    future: BadgeTemplateElement[][]
  }>({ past: [], future: [] })
  const initialElementsRef = useRef<BadgeTemplateElement[]>([]) // snapshot at page load for reset
  const isUndoRedoRef = useRef(false) // flag to skip recording during undo/redo

  const pushHistory = useCallback((prev: BadgeTemplateElement[]) => {
    if (isUndoRedoRef.current) return
    historyRef.current.past.push(JSON.parse(JSON.stringify(prev)))
    historyRef.current.future = [] // clear redo stack on new change
    // Cap history at 100 entries
    if (historyRef.current.past.length > 100) historyRef.current.past.shift()
  }, [])

  const undo = useCallback(() => {
    const { past, future } = historyRef.current
    if (past.length === 0) return
    const prev = past.pop()!
    future.push(JSON.parse(JSON.stringify(elements)))
    isUndoRedoRef.current = true
    setElements(prev)
    setTimeout(() => {
      isUndoRedoRef.current = false
    }, 0)
  }, [elements])

  const redo = useCallback(() => {
    const { past, future } = historyRef.current
    if (future.length === 0) return
    const next = future.pop()!
    past.push(JSON.parse(JSON.stringify(elements)))
    isUndoRedoRef.current = true
    setElements(next)
    setTimeout(() => {
      isUndoRedoRef.current = false
    }, 0)
  }, [elements])

  const resetToInitial = useCallback(() => {
    pushHistory(elements)
    setElements(JSON.parse(JSON.stringify(initialElementsRef.current)))
    setSelectedId(null)
    toast.success('Reset to last saved state')
  }, [elements, pushHistory])

  // Refs for keyboard shortcut access to functions defined later
  const handleSaveRef = useRef<() => void>(() => {})
  const handlePreviewRef = useRef<() => void>(() => {})

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Don't handle shortcuts when typing in an input
      const tag = (e.target as HTMLElement)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return

      const mod = e.metaKey || e.ctrlKey
      if (mod && e.key === 's') {
        e.preventDefault()
        handleSaveRef.current()
      }
      if (mod && e.key === 'p' && isEditing) {
        e.preventDefault()
        handlePreviewRef.current()
      }
      if (mod && e.key === 'z' && !e.shiftKey) {
        e.preventDefault()
        undo()
      }
      if (mod && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault()
        redo()
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId) {
        e.preventDefault()
        setElements((prev) => {
          if (!isUndoRedoRef.current) {
            historyRef.current.past.push(JSON.parse(JSON.stringify(prev)))
            historyRef.current.future = []
          }
          return prev.filter((el) => el.id !== selectedId)
        })
        setSelectedId(null)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [undo, redo, selectedId])

  // Snapping
  const SNAP_THRESHOLD = 3 // mm
  const [snapping, setSnapping] = useState(true)
  const [snapGuides, setSnapGuides] = useState<{ x?: number; y?: number }[]>([])

  const computeSnap = useCallback(
    (elId: string, rawX: number, rawY: number, elW: number, elH: number) => {
      if (!snapping)
        return { x: rawX, y: rawY, guides: [] as { x?: number; y?: number }[] }

      const guides: { x?: number; y?: number }[] = []
      let snappedX = rawX,
        snappedY = rawY

      // Snap targets: canvas edges, canvas center, other elements' edges and centers
      const xTargets: number[] = [0, badgeWidth / 2, badgeWidth]
      const yTargets: number[] = [0, badgeHeight / 2, badgeHeight]

      for (const el of elements) {
        if (el.type === 'CUSTOM_PATH' || el.id === elId) continue
        xTargets.push(el.x, el.x + el.width / 2, el.x + el.width)
        yTargets.push(el.y, el.y + el.height / 2, el.y + el.height)
      }

      // Element edges and center
      const elLeft = rawX,
        elCenter = rawX + elW / 2,
        elRight = rawX + elW
      const elTop = rawY,
        elMiddle = rawY + elH / 2,
        elBottom = rawY + elH

      // Check X snaps (left edge, center, right edge)
      for (const target of xTargets) {
        if (Math.abs(elLeft - target) < SNAP_THRESHOLD) {
          snappedX = target
          guides.push({ x: target })
          break
        }
        if (Math.abs(elCenter - target) < SNAP_THRESHOLD) {
          snappedX = target - elW / 2
          guides.push({ x: target })
          break
        }
        if (Math.abs(elRight - target) < SNAP_THRESHOLD) {
          snappedX = target - elW
          guides.push({ x: target })
          break
        }
      }

      // Check Y snaps (top edge, center, bottom edge)
      for (const target of yTargets) {
        if (Math.abs(elTop - target) < SNAP_THRESHOLD) {
          snappedY = target
          guides.push({ y: target })
          break
        }
        if (Math.abs(elMiddle - target) < SNAP_THRESHOLD) {
          snappedY = target - elH / 2
          guides.push({ y: target })
          break
        }
        if (Math.abs(elBottom - target) < SNAP_THRESHOLD) {
          snappedY = target - elH
          guides.push({ y: target })
          break
        }
      }

      return { x: snappedX, y: snappedY, guides }
    },
    [snapping, badgeWidth, badgeHeight, elements]
  )

  // Drag state
  const [dragging, setDragging] = useState(false)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const canvasRef = useRef<HTMLDivElement>(null)
  const clickedElementRef = useRef(false) // tracks if mouseDown was on an element

  const { clients: storeClients } = useApplicationStore()

  useEffect(() => {
    BadgeTemplateEndpoints.getAvailableFields()
      .then(setAvailableFields)
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (isEditing) {
      setLoading(true)
      BadgeTemplateEndpoints.getTemplate(parseInt(templateId!))
        .then((t) => {
          setName(t.name)
          setDescription(t.description || '')
          setCategory(t.category)
          setOrientation(t.orientation)
          setBadgeWidth(t.width)
          setBadgeHeight(t.height)
          setCardSize(matchCardSize(t.width, t.height))
          setBackgroundColor(t.backgroundColor || '#ffffff')
          setIsGlobal(t.isGlobal !== false)
          setSelectedClientIds(t.clientIds || [])
          if (t.layoutDefinition) {
            try {
              const loadedElements = t.layoutDefinition.elements || []
              setElements(loadedElements)
              initialElementsRef.current = JSON.parse(
                JSON.stringify(loadedElements)
              )
              historyRef.current = { past: [], future: [] }
              // Load image data for IMAGE elements that have imageId but no imageData
              loadedElements.forEach((el, i) => {
                if (el.type === 'IMAGE' && el.imageId && !el.imageData) {
                  BadgeTemplateEndpoints.getImage(el.imageId)
                    .then((img) => {
                      setElements((prev) =>
                        prev.map((e, j) =>
                          j === i && e.imageId === el.imageId
                            ? { ...e, imageData: img.data }
                            : e
                        )
                      )
                    })
                    .catch(() => {})
                }
              })
            } catch {
              setElements([])
            }
          }
        })
        .catch((error) => toast.error(extractErrorMessage(error, 'Failed to load template')))
        .finally(() => setLoading(false))
    }
  }, [templateId])

  const addElement = (preset: (typeof ELEMENT_PRESETS)[0]) => {
    const id = generateElementId()
    // Stagger position so new elements don't stack on top of each other
    const offset = elements.length * 15
    const newEl: BadgeTemplateElement = {
      id,
      type: preset.type,
      x: 20 + (offset % 100),
      y: 20 + (offset % 200),
      width: 0,
      height: 0,
      ...preset.defaults,
    }
    pushHistory(elements)
    setElements((prev) => [...prev, newEl])
    setSelectedId(id)
  }

  // Track whether a drag is in progress to batch drag moves into one history entry
  const dragHistoryPushedRef = useRef(false)

  const updateElement = (
    id: string,
    updates: Partial<BadgeTemplateElement>
  ) => {
    if (!dragHistoryPushedRef.current && !isUndoRedoRef.current) {
      pushHistory(elements)
    }
    setElements((prev) =>
      prev.map((el) => (el.id === id ? { ...el, ...updates } : el))
    )
  }

  const removeElement = (id: string) => {
    pushHistory(elements)
    setElements((prev) => prev.filter((el) => el.id !== id))
    if (selectedId === id) setSelectedId(null)
  }

  const handleCanvasMouseDown = (e: React.MouseEvent, elementId: string) => {
    e.stopPropagation()
    clickedElementRef.current = true
    setSelectedId(elementId)
    const el = elements.find((el) => el.id === elementId)
    if (!el || !canvasRef.current) return

    dragHistoryPushedRef.current = false

    const rect = canvasRef.current.getBoundingClientRect()
    setDragOffset({
      x: e.clientX - rect.left - el.x * scale,
      y: e.clientY - rect.top - el.y * scale,
    })
    setDragging(true)
  }

  const handleCanvasMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!dragging || !selectedId || !canvasRef.current) return
      // Push history once on the first actual move
      if (!dragHistoryPushedRef.current) {
        pushHistory(elements)
        dragHistoryPushedRef.current = true
      }
      const rect = canvasRef.current.getBoundingClientRect()
      const rawX = Math.max(
        0,
        Math.round((e.clientX - rect.left - dragOffset.x) / scale)
      )
      const rawY = Math.max(
        0,
        Math.round((e.clientY - rect.top - dragOffset.y) / scale)
      )
      const el = elements.find((el) => el.id === selectedId)
      const { x, y, guides } = computeSnap(
        selectedId,
        rawX,
        rawY,
        el?.width || 0,
        el?.height || 0
      )
      setSnapGuides(guides)
      updateElement(selectedId, { x, y })
    },
    [dragging, selectedId, dragOffset, computeSnap, elements, pushHistory, scale]
  )

  const handleCanvasMouseUp = () => {
    setDragging(false)
    dragHistoryPushedRef.current = false
    setSnapGuides([])
    // Reset after a short delay so the click handler can check it first
    setTimeout(() => {
      clickedElementRef.current = false
    }, 0)
  }

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Template name is required')
      return
    }

    setSaving(true)
    try {
      // Strip imageData from elements that have imageId (data is stored server-side)
      const cleanElements = elements.map((el) => {
        if (el.imageId && el.imageData) {
          const { imageData, ...rest } = el
          return rest
        }
        return el
      })
      const layoutDefinition = { elements: cleanElements }
      const request = {
        name: name.trim(),
        description: description.trim() || undefined,
        category,
        orientation,
        width: badgeWidth,
        height: badgeHeight,
        backgroundColor,
        layoutDefinition,
        isGlobal,
        clientIds: isGlobal ? [] : selectedClientIds,
      }

      if (isEditing) {
        await BadgeTemplateEndpoints.updateTemplate(
          parseInt(templateId!),
          request
        )
        toast.success('Template saved')
      } else {
        const created = await BadgeTemplateEndpoints.createTemplate(request)
        toast.success('Template created')
        navigate(`/admin/badge-templates/${created.id}/edit`, { replace: true })
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save template')
    } finally {
      setSaving(false)
    }
  }

  handleSaveRef.current = handleSave

  const handlePreview = async () => {
    if (!isEditing) return
    await handleSave()
    BadgePrintingLogic(() =>
      BadgeTemplateEndpoints.previewTemplate(parseInt(templateId!))
    ).catch((error) => toast.error(extractErrorMessage(error, 'Failed to generate preview')))
  }
  handlePreviewRef.current = handlePreview

  const selected = selectedId
    ? elements.find((el) => el.id === selectedId)
    : null

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-center min-h-96">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-6 space-y-4">
      {/* Breadcrumb */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink href="/admin/badge-templates">
              Badge Templates
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage className="font-semibold">
              {isEditing ? name : 'New Template'}
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Top bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate('/admin/badge-templates')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="text-lg font-semibold h-9 border-transparent hover:border-border focus:border-border"
              placeholder="Template name"
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-0.5 border rounded-md px-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              disabled={historyRef.current.past.length === 0}
              onClick={undo}
              title="Undo (Ctrl+Z)"
            >
              <Undo2 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              disabled={historyRef.current.future.length === 0}
              onClick={redo}
              title="Redo (Ctrl+Y)"
            >
              <Redo2 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              disabled={
                historyRef.current.past.length === 0 &&
                historyRef.current.future.length === 0
              }
              onClick={resetToInitial}
              title="Reset all changes"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>
          <Button
            variant={snapping ? 'default' : 'outline'}
            size="icon"
            className="h-8 w-8"
            onClick={() => setSnapping(!snapping)}
            title={snapping ? 'Snapping enabled' : 'Snapping disabled'}
          >
            <Magnet className="h-4 w-4" />
          </Button>
          {isEditing && (
            <Button variant="outline" disabled={saving} onClick={handlePreview}>
              <Eye className="mr-2 h-4 w-4" />
              Preview
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="flex gap-4">
        {/* Left: Element Palette */}
        <Card className="w-[200px] shrink-0 self-start">
          <CardHeader className="p-3 pb-2">
            <CardTitle className="text-sm">Elements</CardTitle>
          </CardHeader>
          <CardContent className="p-2 space-y-1">
            {ELEMENT_PRESETS.map((preset) => {
              const Icon = preset.icon
              return (
                <Button
                  key={preset.type}
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start h-9 text-xs"
                  onClick={() => addElement(preset)}
                >
                  <Icon className="h-3.5 w-3.5 mr-2" />
                  {preset.label}
                </Button>
              )
            })}
            <Separator className="my-2" />
            <div className="space-y-2 px-1">
              <div className="space-y-1">
                <Label className="text-[10px] text-muted-foreground">
                  Description
                </Label>
                <Input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="h-7 text-xs"
                  placeholder="Optional"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] text-muted-foreground">
                  Category
                </Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="h-7 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DEFAULT">Default</SelectItem>
                    <SelectItem value="MODERN">Modern</SelectItem>
                    <SelectItem value="CUSTOM">Custom</SelectItem>
                    <SelectItem value="PREMIUM">Premium</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] text-muted-foreground">
                  Card Size
                </Label>
                <Select
                  value={cardSize}
                  onValueChange={(v) => {
                    setCardSize(v)
                    const card = CARD_SIZES.find((c) => c.value === v)
                    if (card && card.value !== 'CUSTOM') {
                      const [w, h] = dimsForOrientation(card, orientation)
                      setBadgeWidth(w)
                      setBadgeHeight(h)
                    }
                  }}
                >
                  <SelectTrigger className="h-7 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CARD_SIZES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] text-muted-foreground">
                  Orientation
                </Label>
                <Select
                  value={orientation}
                  onValueChange={(v) => {
                    setOrientation(v)
                    const card = CARD_SIZES.find((c) => c.value === cardSize)
                    if (card && card.value !== 'CUSTOM') {
                      const [w, h] = dimsForOrientation(card, v)
                      setBadgeWidth(w)
                      setBadgeHeight(h)
                    } else {
                      // Custom size: just swap the current dimensions.
                      setBadgeWidth(badgeHeight)
                      setBadgeHeight(badgeWidth)
                    }
                  }}
                >
                  <SelectTrigger className="h-7 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PORTRAIT">Portrait</SelectItem>
                    <SelectItem value="LANDSCAPE">Landscape</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] text-muted-foreground">
                  Background
                </Label>
                <ColorPicker
                  color={backgroundColor}
                  onChange={setBackgroundColor}
                />
              </div>
              <Separator className="my-2" />
              <div className="space-y-2">
                <Label className="text-[10px] text-muted-foreground">
                  Visibility
                </Label>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="template-global"
                    checked={isGlobal}
                    onCheckedChange={(checked) => {
                      setIsGlobal(checked === true)
                      if (checked) setSelectedClientIds([])
                    }}
                  />
                  <Label
                    htmlFor="template-global"
                    className="text-[10px] cursor-pointer"
                  >
                    Global (all clients)
                  </Label>
                </div>
                {!isGlobal && (
                  <div className="space-y-1">
                    <Label className="text-[10px] text-muted-foreground">
                      Assign to clients
                    </Label>
                    <div className="max-h-32 overflow-y-auto space-y-1 border rounded p-1.5">
                      {storeClients
                        .filter((c) => !c.dateArchived)
                        .map((client) => (
                          <div
                            key={client.id}
                            className="flex items-center gap-1.5"
                          >
                            <Checkbox
                              id={`client-${client.id}`}
                              checked={selectedClientIds.includes(client.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedClientIds((prev) => [
                                    ...prev,
                                    client.id,
                                  ])
                                } else {
                                  setSelectedClientIds((prev) =>
                                    prev.filter((id) => id !== client.id)
                                  )
                                }
                              }}
                            />
                            <Label
                              htmlFor={`client-${client.id}`}
                              className="text-[10px] cursor-pointer font-normal"
                            >
                              {client.name}
                            </Label>
                          </div>
                        ))}
                      {storeClients.filter((c) => !c.dateArchived).length ===
                        0 && (
                        <p className="text-[10px] text-muted-foreground italic p-1">
                          No clients found
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Center: Canvas */}
        <div className="flex-1 flex flex-col items-center gap-3">
          <div className="bg-muted/30 rounded-lg p-8 inline-block">
            {/* Width label */}
            <div className="flex justify-center mb-2">
              <span className="text-[10px] text-muted-foreground bg-muted px-2 py-0.5 rounded">
                {badgeWidth}mm
              </span>
            </div>
            <div className="flex items-center gap-2">
              {/* Height label */}
              <div className="flex items-center">
                <span className="text-[10px] text-muted-foreground bg-muted px-2 py-0.5 rounded [writing-mode:vertical-lr] rotate-180">
                  {badgeHeight}mm
                </span>
              </div>
              <div
                ref={canvasRef}
                className="relative border-2 border-dashed border-border shadow-lg"
                style={{
                  width: badgeWidth * scale,
                  height: badgeHeight * scale,
                  backgroundColor,
                  cursor: dragging ? 'grabbing' : 'default',
                }}
                onMouseMove={handleCanvasMouseMove}
                onMouseUp={handleCanvasMouseUp}
                onMouseLeave={handleCanvasMouseUp}
                onClick={() => {
                  if (clickedElementRef.current) {
                    clickedElementRef.current = false
                    return
                  }
                  setSelectedId(null)
                }}
              >
                {elements.map((el, index) => {
                  const isSelected = el.id === selectedId

                  // Custom path elements render differently — no bounding box
                  if (el.type === 'CUSTOM_PATH') {
                    const pts = el.points || []
                    if (pts.length < 2) return null
                    return (
                      <React.Fragment key={el.id}>
                        <svg
                          className="absolute inset-0"
                          style={{
                            width: badgeWidth * scale,
                            height: badgeHeight * scale,
                            opacity: el.opacity ?? 1,
                            zIndex: isSelected ? 999 : index + 1,
                            pointerEvents: 'none',
                          }}
                        >
                          <path
                            d={buildCustomPathD(
                              pts,
                              el.closed !== false,
                              scale
                            )}
                            fill={el.color || '#DE7027'}
                            stroke={el.borderColor || 'none'}
                            strokeWidth={
                              el.borderColor ? (el.borderWidth || 1) * scale : 0
                            }
                            style={{
                              pointerEvents: 'all',
                              cursor: isSelected ? 'grab' : 'pointer',
                            }}
                            onMouseDown={(e) => {
                              e.stopPropagation()
                              clickedElementRef.current = true
                              setSelectedId(el.id)
                              const startX = e.clientX,
                                startY = e.clientY
                              const origPts = pts.map((p) => ({ ...p }))
                              dragHistoryPushedRef.current = false
                              const onMove = (me: MouseEvent) => {
                                if (!dragHistoryPushedRef.current) {
                                  pushHistory(elements)
                                  dragHistoryPushedRef.current = true
                                }
                                const dx = (me.clientX - startX) / scale
                                const dy = (me.clientY - startY) / scale
                                const newPts = origPts.map((p) => {
                                  const moved: PathPoint = {
                                    ...p,
                                    x: p.x + dx,
                                    y: p.y + dy,
                                  }
                                  if (p.curve) {
                                    if (p.cx1 != null) moved.cx1 = p.cx1 + dx
                                    if (p.cy1 != null) moved.cy1 = p.cy1 + dy
                                    if (p.cx2 != null) moved.cx2 = p.cx2 + dx
                                    if (p.cy2 != null) moved.cy2 = p.cy2 + dy
                                  }
                                  return moved
                                })
                                updateElement(el.id, { points: newPts })
                              }
                              const onUp = () => {
                                document.removeEventListener(
                                  'mousemove',
                                  onMove
                                )
                                document.removeEventListener('mouseup', onUp)
                                dragHistoryPushedRef.current = false
                                setTimeout(() => {
                                  clickedElementRef.current = false
                                }, 0)
                              }
                              document.addEventListener('mousemove', onMove)
                              document.addEventListener('mouseup', onUp)
                            }}
                          />
                        </svg>
                        {isSelected &&
                          pts.map((pt, pi) => (
                            <React.Fragment key={`${el.id}-pt-${pi}`}>
                              <div
                                className="absolute w-2.5 h-2.5 rounded-full bg-primary border-2 border-white shadow-sm"
                                style={{
                                  left: pt.x * scale - 5,
                                  top: pt.y * scale - 5,
                                  cursor: 'move',
                                  zIndex: 1000,
                                }}
                                onMouseDown={(e) => {
                                  e.stopPropagation()
                                  clickedElementRef.current = true
                                  setSelectedId(el.id)
                                  const startX = e.clientX,
                                    startY = e.clientY
                                  const origX = pt.x,
                                    origY = pt.y
                                  const origCx1 = pt.cx1,
                                    origCy1 = pt.cy1,
                                    origCx2 = pt.cx2,
                                    origCy2 = pt.cy2
                                  dragHistoryPushedRef.current = false
                                  const onMove = (me: MouseEvent) => {
                                    if (!dragHistoryPushedRef.current) {
                                      pushHistory(elements)
                                      dragHistoryPushedRef.current = true
                                    }
                                    const dx = (me.clientX - startX) / scale,
                                      dy = (me.clientY - startY) / scale
                                    const newPts = [...el.points!]
                                    const updated: PathPoint = {
                                      ...newPts[pi],
                                      x: origX + dx,
                                      y: origY + dy,
                                    }
                                    if (pt.curve) {
                                      if (origCx1 != null)
                                        updated.cx1 = origCx1 + dx
                                      if (origCy1 != null)
                                        updated.cy1 = origCy1 + dy
                                      if (origCx2 != null)
                                        updated.cx2 = origCx2 + dx
                                      if (origCy2 != null)
                                        updated.cy2 = origCy2 + dy
                                    }
                                    newPts[pi] = updated
                                    updateElement(el.id, { points: newPts })
                                  }
                                  const onUp = () => {
                                    document.removeEventListener(
                                      'mousemove',
                                      onMove
                                    )
                                    document.removeEventListener(
                                      'mouseup',
                                      onUp
                                    )
                                    dragHistoryPushedRef.current = false
                                    setTimeout(() => {
                                      clickedElementRef.current = false
                                    }, 0)
                                  }
                                  document.addEventListener('mousemove', onMove)
                                  document.addEventListener('mouseup', onUp)
                                }}
                              />
                              {pt.curve && (
                                <>
                                  <svg
                                    className="absolute inset-0 pointer-events-none"
                                    style={{
                                      width: badgeWidth * scale,
                                      height: badgeHeight * scale,
                                      zIndex: 999,
                                    }}
                                  >
                                    <line
                                      x1={pts[pi > 0 ? pi - 1 : 0].x * scale}
                                      y1={pts[pi > 0 ? pi - 1 : 0].y * scale}
                                      x2={(pt.cx1 ?? 0) * scale}
                                      y2={(pt.cy1 ?? 0) * scale}
                                      stroke="#3b82f6"
                                      strokeWidth={1}
                                      strokeDasharray="2,2"
                                    />
                                    <line
                                      x1={pt.x * scale}
                                      y1={pt.y * scale}
                                      x2={(pt.cx2 ?? 0) * scale}
                                      y2={(pt.cy2 ?? 0) * scale}
                                      stroke="#3b82f6"
                                      strokeWidth={1}
                                      strokeDasharray="2,2"
                                    />
                                  </svg>
                                  <div
                                    className="absolute w-2 h-2 rounded-full bg-blue-500 border border-white shadow-sm"
                                    style={{
                                      left: (pt.cx1 ?? 0) * scale - 4,
                                      top: (pt.cy1 ?? 0) * scale - 4,
                                      cursor: 'move',
                                      zIndex: 1001,
                                    }}
                                    onMouseDown={(e) => {
                                      e.stopPropagation()
                                      clickedElementRef.current = true
                                      const startX = e.clientX,
                                        startY = e.clientY,
                                        ox = pt.cx1 ?? 0,
                                        oy = pt.cy1 ?? 0
                                      dragHistoryPushedRef.current = false
                                      const onMove = (me: MouseEvent) => {
                                        if (!dragHistoryPushedRef.current) {
                                          pushHistory(elements)
                                          dragHistoryPushedRef.current = true
                                        }
                                        const newPts = [...el.points!]
                                        newPts[pi] = {
                                          ...newPts[pi],
                                          cx1:
                                            ox + (me.clientX - startX) / scale,
                                          cy1:
                                            oy + (me.clientY - startY) / scale,
                                        }
                                        updateElement(el.id, { points: newPts })
                                      }
                                      const onUp = () => {
                                        document.removeEventListener(
                                          'mousemove',
                                          onMove
                                        )
                                        document.removeEventListener(
                                          'mouseup',
                                          onUp
                                        )
                                        dragHistoryPushedRef.current = false
                                        setTimeout(() => {
                                          clickedElementRef.current = false
                                        }, 0)
                                      }
                                      document.addEventListener(
                                        'mousemove',
                                        onMove
                                      )
                                      document.addEventListener('mouseup', onUp)
                                    }}
                                  />
                                  <div
                                    className="absolute w-2 h-2 rounded-full bg-blue-500 border border-white shadow-sm"
                                    style={{
                                      left: (pt.cx2 ?? 0) * scale - 4,
                                      top: (pt.cy2 ?? 0) * scale - 4,
                                      cursor: 'move',
                                      zIndex: 1001,
                                    }}
                                    onMouseDown={(e) => {
                                      e.stopPropagation()
                                      clickedElementRef.current = true
                                      const startX = e.clientX,
                                        startY = e.clientY,
                                        ox = pt.cx2 ?? 0,
                                        oy = pt.cy2 ?? 0
                                      dragHistoryPushedRef.current = false
                                      const onMove = (me: MouseEvent) => {
                                        if (!dragHistoryPushedRef.current) {
                                          pushHistory(elements)
                                          dragHistoryPushedRef.current = true
                                        }
                                        const newPts = [...el.points!]
                                        newPts[pi] = {
                                          ...newPts[pi],
                                          cx2:
                                            ox + (me.clientX - startX) / scale,
                                          cy2:
                                            oy + (me.clientY - startY) / scale,
                                        }
                                        updateElement(el.id, { points: newPts })
                                      }
                                      const onUp = () => {
                                        document.removeEventListener(
                                          'mousemove',
                                          onMove
                                        )
                                        document.removeEventListener(
                                          'mouseup',
                                          onUp
                                        )
                                        dragHistoryPushedRef.current = false
                                        setTimeout(() => {
                                          clickedElementRef.current = false
                                        }, 0)
                                      }
                                      document.addEventListener(
                                        'mousemove',
                                        onMove
                                      )
                                      document.addEventListener('mouseup', onUp)
                                    }}
                                  />
                                </>
                              )}
                            </React.Fragment>
                          ))}
                      </React.Fragment>
                    )
                  }

                  // Regular elements with bounding box
                  return (
                    <div
                      key={el.id}
                      className={cn(
                        'absolute transition-shadow',
                        isSelected
                          ? 'border-2 border-primary shadow-md'
                          : 'border border-transparent hover:border-muted-foreground/30',
                        dragging && isSelected
                          ? 'cursor-grabbing'
                          : 'cursor-grab'
                      )}
                      style={{
                        left: el.x * scale,
                        top: el.y * scale,
                        width: el.width * scale,
                        height: el.height * scale,
                        zIndex: isSelected ? 999 : index + 1,
                        opacity: el.opacity ?? 1,
                        backgroundColor:
                          el.type === 'RECT'
                            ? el.color || '#DE7027'
                            : el.type === 'TEXT' && el.backgroundColor
                              ? el.backgroundColor
                              : undefined,
                        borderRadius:
                          el.type === 'RECT' && el.borderRadius
                            ? el.borderRadius * scale
                            : undefined,
                      }}
                      onMouseDown={(e) => handleCanvasMouseDown(e, el.id)}
                    >
                      {/* Visual representation */}
                      {el.type === 'PHOTO' && (
                        <div className="w-full h-full flex items-center justify-center">
                          <div
                            className="bg-gray-200 rounded-full flex items-center justify-center overflow-hidden"
                            style={{
                              width: Math.min(el.width, el.height) * scale,
                              height: Math.min(el.width, el.height) * scale,
                            }}
                          >
                            <User
                              className="text-gray-400"
                              style={{ width: '40%', height: '40%' }}
                            />
                          </div>
                        </div>
                      )}
                      {el.type === 'TEXT' && (
                        <div
                          className="w-full h-full flex items-center overflow-hidden px-1"
                          style={{
                            fontSize: (el.fontSize || 12) * (25.4 / 72) * scale,
                            fontWeight: el.fontWeight === 'BOLD' ? 700 : 400,
                            color: el.color || '#000',
                            textAlign: (el.align?.toLowerCase() ||
                              'center') as any,
                            justifyContent:
                              el.align === 'LEFT'
                                ? 'flex-start'
                                : el.align === 'RIGHT'
                                  ? 'flex-end'
                                  : 'center',
                            textTransform: el.uppercase ? 'uppercase' : 'none',
                          }}
                        >
                          <span className="truncate">{el.field || 'Text'}</span>
                        </div>
                      )}
                      {el.type === 'BARCODE' && (
                        <div className="w-full h-full bg-white flex items-center justify-center border border-gray-300">
                          <QrCode
                            className="text-gray-600"
                            style={{ width: '60%', height: '60%' }}
                          />
                        </div>
                      )}
                      {el.type === 'LOGO' && (
                        <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                          <ImageIcon
                            className="text-gray-400"
                            style={{ width: '40%', height: '40%' }}
                          />
                        </div>
                      )}
                      {el.type === 'LINE' && (
                        <div
                          className="w-full"
                          style={{
                            height: Math.max(1, (el.lineWidth || 1) * scale),
                            backgroundColor: el.color || '#ccc',
                            marginTop:
                              (el.height * scale -
                                Math.max(1, (el.lineWidth || 1) * scale)) /
                              2,
                          }}
                        />
                      )}
                      {el.type === 'ELLIPSE' && (
                        <div
                          className="w-full h-full rounded-[50%]"
                          style={{
                            backgroundColor: el.color || '#DE7027',
                            border: el.borderColor
                              ? `${el.borderWidth || 1}px solid ${el.borderColor}`
                              : undefined,
                          }}
                        />
                      )}
                      {el.type === 'PATH' && (
                        <svg
                          className="w-full h-full"
                          viewBox="0 0 1 1"
                          preserveAspectRatio="none"
                        >
                          <path
                            d={
                              SHAPE_SVG_PATHS[el.shape as PathShape] ||
                              SHAPE_SVG_PATHS.DIAMOND
                            }
                            fill={el.color || '#DE7027'}
                            stroke={el.borderColor || 'none'}
                            strokeWidth={
                              el.borderColor
                                ? (el.borderWidth || 1) /
                                  Math.max(el.width, el.height) /
                                  scale
                                : 0
                            }
                          />
                        </svg>
                      )}
                      {el.type === 'IMAGE' && (
                        <div
                          className="w-full h-full bg-gray-100 flex items-center justify-center overflow-hidden"
                          style={{
                            borderRadius: el.borderRadius
                              ? el.borderRadius * scale
                              : undefined,
                          }}
                        >
                          {el.imageData ? (
                            <img
                              src={`data:image/png;base64,${el.imageData}`}
                              alt=""
                              className="w-full h-full object-cover"
                              draggable={false}
                            />
                          ) : (
                            <Upload
                              className="text-gray-400"
                              style={{ width: '30%', height: '30%' }}
                            />
                          )}
                        </div>
                      )}
                      {/* Selection label */}
                      {isSelected && (
                        <div className="absolute -top-5 left-0 text-[9px] bg-primary text-primary-foreground px-1 rounded-t whitespace-nowrap">
                          {el.type}
                          {el.field
                            ? `: ${el.field}`
                            : el.shape
                              ? `: ${el.shape}`
                              : ''}
                        </div>
                      )}
                    </div>
                  )
                })}

                {/* Snap guide lines */}
                {snapGuides.map((g, i) => (
                  <React.Fragment key={i}>
                    {g.x != null && (
                      <div
                        className="absolute top-0 pointer-events-none"
                        style={{
                          left: g.x * scale,
                          width: 0,
                          height: badgeHeight * scale,
                          borderLeft: '1px dashed #3b82f6',
                          zIndex: 9999,
                        }}
                      />
                    )}
                    {g.y != null && (
                      <div
                        className="absolute left-0 pointer-events-none"
                        style={{
                          top: g.y * scale,
                          width: badgeWidth * scale,
                          height: 0,
                          borderTop: '1px dashed #3b82f6',
                          zIndex: 9999,
                        }}
                      />
                    )}
                  </React.Fragment>
                ))}

                {elements.length === 0 && (
                  <div className="absolute inset-0 flex items-center justify-center text-muted-foreground text-sm">
                    Add elements from the palette
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Dimension inputs */}
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <Label className="text-[10px]">W</Label>
              <Input
                type="number"
                step="0.01"
                value={badgeWidth}
                onChange={(e) => {
                  setBadgeWidth(parseFloat(e.target.value) || 0)
                  setCardSize('CUSTOM')
                }}
                className="h-7 w-16 text-xs"
              />
              <span>mm</span>
            </div>
            <span>×</span>
            <div className="flex items-center gap-1.5">
              <Label className="text-[10px]">H</Label>
              <Input
                type="number"
                step="0.01"
                value={badgeHeight}
                onChange={(e) => {
                  setBadgeHeight(parseFloat(e.target.value) || 0)
                  setCardSize('CUSTOM')
                }}
                className="h-7 w-16 text-xs"
              />
              <span>mm</span>
            </div>
          </div>
        </div>

        {/* Right: Property Panel */}
        <Card className="w-[240px] shrink-0 self-start">
          <CardHeader className="p-3 pb-2">
            <CardTitle className="text-sm">Properties</CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-0">
            {!selected ? (
              <p className="text-xs text-muted-foreground italic py-4">
                Select an element to edit its properties
              </p>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Badge variant="outline" className="text-xs">
                    {selected.type}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive"
                    onClick={() => removeElement(selected.id)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>

                <Separator />

                {/* Position */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-[10px]">X</Label>
                    <Input
                      type="number"
                      value={selected.x}
                      onChange={(e) =>
                        updateElement(selected.id, {
                          x: parseInt(e.target.value) || 0,
                        })
                      }
                      className="h-7 text-xs"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px]">Y</Label>
                    <Input
                      type="number"
                      value={selected.y}
                      onChange={(e) =>
                        updateElement(selected.id, {
                          y: parseInt(e.target.value) || 0,
                        })
                      }
                      className="h-7 text-xs"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px]">Width</Label>
                    <Input
                      type="number"
                      value={selected.width}
                      onChange={(e) =>
                        updateElement(selected.id, {
                          width: parseInt(e.target.value) || 0,
                        })
                      }
                      className="h-7 text-xs"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px]">Height</Label>
                    <Input
                      type="number"
                      value={selected.height}
                      onChange={(e) =>
                        updateElement(selected.id, {
                          height: parseInt(e.target.value) || 0,
                        })
                      }
                      className="h-7 text-xs"
                    />
                  </div>
                </div>

                {/* TEXT properties */}
                {selected.type === 'TEXT' && (
                  <>
                    <Separator />
                    <div className="space-y-2">
                      <div className="space-y-1">
                        <Label className="text-[10px]">Field Binding</Label>
                        <Select
                          value={selected.field || ''}
                          onValueChange={(v) =>
                            updateElement(selected.id, { field: v })
                          }
                        >
                          <SelectTrigger className="h-7 text-xs">
                            <SelectValue placeholder="Select field" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableFields.map((f) => (
                              <SelectItem key={f.key} value={f.key}>
                                {f.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-[10px]">Font Size</Label>
                          <Input
                            type="number"
                            value={selected.fontSize || 12}
                            onChange={(e) =>
                              updateElement(selected.id, {
                                fontSize: parseInt(e.target.value) || 12,
                              })
                            }
                            className="h-7 text-xs"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[10px]">Weight</Label>
                          <Select
                            value={selected.fontWeight || 'NORMAL'}
                            onValueChange={(v: any) =>
                              updateElement(selected.id, { fontWeight: v })
                            }
                          >
                            <SelectTrigger className="h-7 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="NORMAL">Normal</SelectItem>
                              <SelectItem value="BOLD">Bold</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px]">Align</Label>
                        <Select
                          value={selected.align || 'CENTER'}
                          onValueChange={(v: any) =>
                            updateElement(selected.id, { align: v })
                          }
                        >
                          <SelectTrigger className="h-7 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="LEFT">Left</SelectItem>
                            <SelectItem value="CENTER">Center</SelectItem>
                            <SelectItem value="RIGHT">Right</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px]">Text Color</Label>
                        <ColorPicker
                          color={selected.color || '#000000'}
                          onChange={(c) =>
                            updateElement(selected.id, { color: c })
                          }
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px]">Background</Label>
                        <ColorPicker
                          color={selected.backgroundColor || ''}
                          onChange={(c) =>
                            updateElement(selected.id, { backgroundColor: c })
                          }
                        />
                      </div>
                      <div className="flex items-center gap-2 pt-1">
                        <Checkbox
                          id="text-uppercase"
                          checked={!!selected.uppercase}
                          onCheckedChange={(checked) =>
                            updateElement(selected.id, {
                              uppercase: checked === true,
                            })
                          }
                        />
                        <Label
                          htmlFor="text-uppercase"
                          className="text-[10px] cursor-pointer"
                        >
                          Uppercase
                        </Label>
                      </div>
                    </div>
                  </>
                )}

                {/* RECT properties */}
                {selected.type === 'RECT' && (
                  <>
                    <Separator />
                    <div className="space-y-2">
                      <div className="space-y-1">
                        <Label className="text-[10px]">Fill Color</Label>
                        <ColorPicker
                          color={selected.color || '#DE7027'}
                          onChange={(c) =>
                            updateElement(selected.id, { color: c })
                          }
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px]">
                          Border Radius (mm)
                        </Label>
                        <Input
                          type="number"
                          value={selected.borderRadius || 0}
                          onChange={(e) =>
                            updateElement(selected.id, {
                              borderRadius: parseFloat(e.target.value) || 0,
                            })
                          }
                          className="h-7 text-xs"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px]">Opacity</Label>
                        <Input
                          type="number"
                          min={0}
                          max={1}
                          step={0.1}
                          value={selected.opacity ?? 1}
                          onChange={(e) =>
                            updateElement(selected.id, {
                              opacity: parseFloat(e.target.value) || 1,
                            })
                          }
                          className="h-7 text-xs"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px]">Border Color</Label>
                        <ColorPicker
                          color={selected.borderColor || ''}
                          onChange={(c) =>
                            updateElement(selected.id, { borderColor: c })
                          }
                        />
                      </div>
                      {selected.borderColor && (
                        <div className="space-y-1">
                          <Label className="text-[10px]">Border Width</Label>
                          <Input
                            type="number"
                            value={selected.borderWidth || 1}
                            onChange={(e) =>
                              updateElement(selected.id, {
                                borderWidth: parseFloat(e.target.value) || 1,
                              })
                            }
                            className="h-7 text-xs"
                          />
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* ELLIPSE properties */}
                {selected.type === 'ELLIPSE' && (
                  <>
                    <Separator />
                    <div className="space-y-2">
                      <div className="space-y-1">
                        <Label className="text-[10px]">Fill Color</Label>
                        <ColorPicker
                          color={selected.color || '#DE7027'}
                          onChange={(c) =>
                            updateElement(selected.id, { color: c })
                          }
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px]">Opacity</Label>
                        <Input
                          type="number"
                          min={0}
                          max={1}
                          step={0.1}
                          value={selected.opacity ?? 1}
                          onChange={(e) =>
                            updateElement(selected.id, {
                              opacity: parseFloat(e.target.value) || 1,
                            })
                          }
                          className="h-7 text-xs"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px]">Border Color</Label>
                        <ColorPicker
                          color={selected.borderColor || ''}
                          onChange={(c) =>
                            updateElement(selected.id, { borderColor: c })
                          }
                        />
                      </div>
                      {selected.borderColor && (
                        <div className="space-y-1">
                          <Label className="text-[10px]">Border Width</Label>
                          <Input
                            type="number"
                            value={selected.borderWidth || 1}
                            onChange={(e) =>
                              updateElement(selected.id, {
                                borderWidth: parseFloat(e.target.value) || 1,
                              })
                            }
                            className="h-7 text-xs"
                          />
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* PATH/Shape properties */}
                {selected.type === 'PATH' && (
                  <>
                    <Separator />
                    <div className="space-y-2">
                      <div className="space-y-1">
                        <Label className="text-[10px]">Shape</Label>
                        <Select
                          value={selected.shape || 'STAR'}
                          onValueChange={(v: any) =>
                            updateElement(selected.id, { shape: v })
                          }
                        >
                          <SelectTrigger className="h-7 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PATH_SHAPES.map((s) => (
                              <SelectItem key={s.value} value={s.value}>
                                {s.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px]">Fill Color</Label>
                        <ColorPicker
                          color={selected.color || '#DE7027'}
                          onChange={(c) =>
                            updateElement(selected.id, { color: c })
                          }
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px]">Opacity</Label>
                        <Input
                          type="number"
                          min={0}
                          max={1}
                          step={0.1}
                          value={selected.opacity ?? 1}
                          onChange={(e) =>
                            updateElement(selected.id, {
                              opacity: parseFloat(e.target.value) || 1,
                            })
                          }
                          className="h-7 text-xs"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px]">Border Color</Label>
                        <ColorPicker
                          color={selected.borderColor || ''}
                          onChange={(c) =>
                            updateElement(selected.id, { borderColor: c })
                          }
                        />
                      </div>
                      {selected.borderColor && (
                        <div className="space-y-1">
                          <Label className="text-[10px]">Border Width</Label>
                          <Input
                            type="number"
                            value={selected.borderWidth || 1}
                            onChange={(e) =>
                              updateElement(selected.id, {
                                borderWidth: parseFloat(e.target.value) || 1,
                              })
                            }
                            className="h-7 text-xs"
                          />
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* IMAGE properties */}
                {selected.type === 'IMAGE' && (
                  <>
                    <Separator />
                    <div className="space-y-2">
                      <div className="space-y-1">
                        <Label className="text-[10px]">Upload Image</Label>
                        <div className="relative">
                          <input
                            type="file"
                            accept="image/*"
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            onChange={async (e) => {
                              const file = e.target.files?.[0]
                              if (!file) return
                              const reader = new FileReader()
                              reader.onload = async () => {
                                const base64 = (reader.result as string).split(
                                  ','
                                )[1]
                                try {
                                  const result =
                                    await BadgeTemplateEndpoints.uploadImage(
                                      base64,
                                      file.type
                                    )
                                  updateElement(selected.id, {
                                    imageId: result.id,
                                    imageData: base64,
                                  })
                                  toast.success('Image uploaded')
                                } catch (error) {
                                  toast.error(extractErrorMessage(error, 'Failed to upload image'))
                                }
                              }
                              reader.readAsDataURL(file)
                            }}
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full h-7 text-xs"
                          >
                            <Upload className="h-3 w-3 mr-1" />
                            {selected.imageId
                              ? 'Replace Image'
                              : 'Choose Image'}
                          </Button>
                        </div>
                        {selected.imageId && (
                          <p className="text-[10px] text-muted-foreground">
                            Image ID: {selected.imageId}
                          </p>
                        )}
                        {selected.imageId && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full h-6 text-[10px] text-destructive"
                            onClick={() =>
                              updateElement(selected.id, {
                                imageId: undefined,
                                imageData: undefined,
                              })
                            }
                          >
                            Remove Image
                          </Button>
                        )}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px]">
                          Border Radius (mm)
                        </Label>
                        <Input
                          type="number"
                          value={selected.borderRadius || 0}
                          onChange={(e) =>
                            updateElement(selected.id, {
                              borderRadius: parseFloat(e.target.value) || 0,
                            })
                          }
                          className="h-7 text-xs"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px]">Opacity</Label>
                        <Input
                          type="number"
                          min={0}
                          max={1}
                          step={0.1}
                          value={selected.opacity ?? 1}
                          onChange={(e) =>
                            updateElement(selected.id, {
                              opacity: parseFloat(e.target.value) || 1,
                            })
                          }
                          className="h-7 text-xs"
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* LINE properties */}
                {selected.type === 'LINE' && (
                  <>
                    <Separator />
                    <div className="space-y-2">
                      <div className="space-y-1">
                        <Label className="text-[10px]">Color</Label>
                        <ColorPicker
                          color={selected.color || '#cccccc'}
                          onChange={(c) =>
                            updateElement(selected.id, { color: c })
                          }
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px]">Thickness</Label>
                        <Input
                          type="number"
                          value={selected.lineWidth || 1}
                          onChange={(e) =>
                            updateElement(selected.id, {
                              lineWidth: parseFloat(e.target.value) || 1,
                            })
                          }
                          className="h-7 text-xs"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px]">Opacity</Label>
                        <Input
                          type="number"
                          min={0}
                          max={1}
                          step={0.1}
                          value={selected.opacity ?? 1}
                          onChange={(e) =>
                            updateElement(selected.id, {
                              opacity: parseFloat(e.target.value) || 1,
                            })
                          }
                          className="h-7 text-xs"
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* CUSTOM_PATH properties */}
                {selected.type === 'CUSTOM_PATH' && (
                  <>
                    <Separator />
                    <div className="space-y-2">
                      <div className="space-y-1">
                        <Label className="text-[10px]">Fill Color</Label>
                        <ColorPicker
                          color={selected.color || '#DE7027'}
                          onChange={(c) =>
                            updateElement(selected.id, { color: c })
                          }
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px]">Border Color</Label>
                        <ColorPicker
                          color={selected.borderColor || ''}
                          onChange={(c) =>
                            updateElement(selected.id, { borderColor: c })
                          }
                        />
                      </div>
                      {selected.borderColor && (
                        <div className="space-y-1">
                          <Label className="text-[10px]">Border Width</Label>
                          <Input
                            type="number"
                            value={selected.borderWidth || 1}
                            onChange={(e) =>
                              updateElement(selected.id, {
                                borderWidth: parseFloat(e.target.value) || 1,
                              })
                            }
                            className="h-7 text-xs"
                          />
                        </div>
                      )}
                      <div className="space-y-1">
                        <Label className="text-[10px]">Opacity</Label>
                        <Input
                          type="number"
                          min={0}
                          max={1}
                          step={0.1}
                          value={selected.opacity ?? 1}
                          onChange={(e) =>
                            updateElement(selected.id, {
                              opacity: parseFloat(e.target.value) || 1,
                            })
                          }
                          className="h-7 text-xs"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="path-closed"
                          checked={selected.closed !== false}
                          onCheckedChange={(checked) =>
                            updateElement(selected.id, {
                              closed: checked === true,
                            })
                          }
                        />
                        <Label
                          htmlFor="path-closed"
                          className="text-[10px] cursor-pointer"
                        >
                          Close path
                        </Label>
                      </div>
                      <Separator />
                      <div className="flex items-center justify-between">
                        <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">
                          Points ({selected.points?.length || 0})
                        </Label>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-6 text-[10px] px-2"
                          onClick={() => {
                            const pts = selected.points || []
                            // Add a new point at the midpoint of the last segment
                            if (pts.length >= 2) {
                              const last = pts[pts.length - 1]
                              const prev = pts[pts.length - 2]
                              const newPt: PathPoint = {
                                x: (last.x + prev.x) / 2,
                                y: (last.y + prev.y) / 2,
                              }
                              const newPts = [
                                ...pts.slice(0, -1),
                                newPt,
                                pts[pts.length - 1],
                              ]
                              updateElement(selected.id, { points: newPts })
                            } else {
                              updateElement(selected.id, {
                                points: [
                                  ...pts,
                                  { x: badgeWidth / 2, y: badgeHeight / 2 },
                                ],
                              })
                            }
                          }}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Add
                        </Button>
                      </div>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {(selected.points || []).map((pt, i) => {
                          const updatePt = (updates: Partial<PathPoint>) => {
                            const newPts = [...(selected.points || [])]
                            newPts[i] = { ...newPts[i], ...updates }
                            updateElement(selected.id, { points: newPts })
                          }
                          return (
                            <div
                              key={i}
                              className="bg-muted/50 rounded p-1.5 space-y-1"
                            >
                              <div className="flex items-center gap-1">
                                <span className="w-4 text-[10px] text-muted-foreground font-mono shrink-0">
                                  {i}
                                </span>
                                <div className="flex gap-1 flex-1">
                                  <Input
                                    type="number"
                                    step={0.5}
                                    value={pt.x}
                                    onChange={(e) =>
                                      updatePt({
                                        x: parseFloat(e.target.value) || 0,
                                      })
                                    }
                                    className="h-5 text-[10px] px-1 w-16"
                                    title="X (mm)"
                                  />
                                  <Input
                                    type="number"
                                    step={0.5}
                                    value={pt.y}
                                    onChange={(e) =>
                                      updatePt({
                                        y: parseFloat(e.target.value) || 0,
                                      })
                                    }
                                    className="h-5 text-[10px] px-1 w-16"
                                    title="Y (mm)"
                                  />
                                </div>
                                {i > 0 && (
                                  <Button
                                    variant={pt.curve ? 'default' : 'outline'}
                                    size="sm"
                                    className="h-5 text-[9px] px-1.5 shrink-0"
                                    onClick={() => {
                                      const newPts = [
                                        ...(selected.points || []),
                                      ]
                                      if (pt.curve) {
                                        newPts[i] = { x: pt.x, y: pt.y }
                                      } else {
                                        const prev = newPts[i - 1]
                                        const dx = pt.x - prev.x,
                                          dy = pt.y - prev.y
                                        const dist = Math.sqrt(
                                          dx * dx + dy * dy
                                        )
                                        const off = Math.max(10, dist * 0.3)
                                        const nx = (-dy / (dist || 1)) * off,
                                          ny = (dx / (dist || 1)) * off
                                        newPts[i] = {
                                          ...pt,
                                          curve: true,
                                          cx1:
                                            Math.round(
                                              (prev.x + dx * 0.33 + nx) * 100
                                            ) / 100,
                                          cy1:
                                            Math.round(
                                              (prev.y + dy * 0.33 + ny) * 100
                                            ) / 100,
                                          cx2:
                                            Math.round(
                                              (prev.x + dx * 0.66 + nx) * 100
                                            ) / 100,
                                          cy2:
                                            Math.round(
                                              (prev.y + dy * 0.66 + ny) * 100
                                            ) / 100,
                                        }
                                      }
                                      updateElement(selected.id, {
                                        points: newPts,
                                      })
                                    }}
                                  >
                                    {pt.curve ? 'Curve' : 'Line'}
                                  </Button>
                                )}
                                {(selected.points || []).length > 2 && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-5 w-5 p-0 text-destructive shrink-0"
                                    onClick={() =>
                                      updateElement(selected.id, {
                                        points: (selected.points || []).filter(
                                          (_, j) => j !== i
                                        ),
                                      })
                                    }
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>
                              {pt.curve && (
                                <div className="ml-4 space-y-1">
                                  <div className="flex items-center gap-1">
                                    <span className="text-[9px] text-blue-500 w-6 shrink-0">
                                      CP1
                                    </span>
                                    <Input
                                      type="number"
                                      step={0.5}
                                      value={pt.cx1 ?? 0}
                                      onChange={(e) =>
                                        updatePt({
                                          cx1: parseFloat(e.target.value) || 0,
                                        })
                                      }
                                      className="h-5 text-[10px] px-1 w-16"
                                      title="Control Point 1 X"
                                    />
                                    <Input
                                      type="number"
                                      step={0.5}
                                      value={pt.cy1 ?? 0}
                                      onChange={(e) =>
                                        updatePt({
                                          cy1: parseFloat(e.target.value) || 0,
                                        })
                                      }
                                      className="h-5 text-[10px] px-1 w-16"
                                      title="Control Point 1 Y"
                                    />
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <span className="text-[9px] text-blue-500 w-6 shrink-0">
                                      CP2
                                    </span>
                                    <Input
                                      type="number"
                                      step={0.5}
                                      value={pt.cx2 ?? 0}
                                      onChange={(e) =>
                                        updatePt({
                                          cx2: parseFloat(e.target.value) || 0,
                                        })
                                      }
                                      className="h-5 text-[10px] px-1 w-16"
                                      title="Control Point 2 X"
                                    />
                                    <Input
                                      type="number"
                                      step={0.5}
                                      value={pt.cy2 ?? 0}
                                      onChange={(e) =>
                                        updatePt({
                                          cy2: parseFloat(e.target.value) || 0,
                                        })
                                      }
                                      className="h-5 text-[10px] px-1 w-16"
                                      title="Control Point 2 Y"
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                      <p className="text-[9px] text-muted-foreground">
                        Drag points on the canvas or edit values directly. All
                        coordinates in mm.
                      </p>
                    </div>
                  </>
                )}

                {/* Overridable properties */}
                {OVERRIDABLE_PROPERTIES[selected.type] && (
                  <>
                    <Separator />
                    <div className="space-y-2">
                      <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">
                        Overridable Properties
                      </Label>
                      <p className="text-[9px] text-muted-foreground">
                        Allow these properties to be customized at the
                        event/client level
                      </p>
                      {OVERRIDABLE_PROPERTIES[selected.type].map((prop) => {
                        const existingOverride = selected.overrides?.find(
                          (o) => o.property === prop.property
                        )
                        const isChecked = !!existingOverride
                        return (
                          <div key={prop.property} className="space-y-1">
                            <div className="flex items-center gap-2">
                              <Checkbox
                                id={`override-${selected.id}-${prop.property}`}
                                checked={isChecked}
                                onCheckedChange={(checked) => {
                                  const currentOverrides =
                                    selected.overrides || []
                                  if (checked) {
                                    updateElement(selected.id, {
                                      overrides: [
                                        ...currentOverrides,
                                        {
                                          property: prop.property,
                                          label: prop.label,
                                        },
                                      ],
                                    })
                                  } else {
                                    updateElement(selected.id, {
                                      overrides: currentOverrides.filter(
                                        (o) => o.property !== prop.property
                                      ),
                                    })
                                  }
                                }}
                              />
                              <Label
                                htmlFor={`override-${selected.id}-${prop.property}`}
                                className="text-[10px] cursor-pointer"
                              >
                                {prop.label}
                              </Label>
                            </div>
                            {isChecked && (
                              <Input
                                value={existingOverride!.label}
                                onChange={(e) => {
                                  const currentOverrides =
                                    selected.overrides || []
                                  updateElement(selected.id, {
                                    overrides: currentOverrides.map((o) =>
                                      o.property === prop.property
                                        ? { ...o, label: e.target.value }
                                        : o
                                    ),
                                  })
                                }}
                                className="h-6 text-[10px] ml-6"
                                placeholder="Display label"
                              />
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </>
                )}

                {/* Element order */}
                <Separator />
                <div className="flex gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 h-7 text-xs"
                    onClick={() => {
                      const idx = elements.findIndex(
                        (e) => e.id === selected.id
                      )
                      if (idx < elements.length - 1) {
                        const newArr = [...elements]
                        ;[newArr[idx], newArr[idx + 1]] = [
                          newArr[idx + 1],
                          newArr[idx],
                        ]
                        setElements(newArr)
                      }
                    }}
                  >
                    Bring Forward
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 h-7 text-xs"
                    onClick={() => {
                      const idx = elements.findIndex(
                        (e) => e.id === selected.id
                      )
                      if (idx > 0) {
                        const newArr = [...elements]
                        ;[newArr[idx], newArr[idx - 1]] = [
                          newArr[idx - 1],
                          newArr[idx],
                        ]
                        setElements(newArr)
                      }
                    }}
                  >
                    Send Back
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Element List */}
      <Card>
        <CardHeader className="p-3 pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <GripVertical className="h-4 w-4" />
            Elements ({elements.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-2">
          {elements.length === 0 ? (
            <p className="text-xs text-muted-foreground italic p-2">
              No elements yet. Click elements in the palette to add them.
            </p>
          ) : (
            <div className="flex flex-wrap gap-1">
              {elements.map((el) => (
                <Badge
                  key={el.id}
                  variant={el.id === selectedId ? 'default' : 'outline'}
                  className="text-xs cursor-pointer"
                  onClick={() => setSelectedId(el.id)}
                >
                  {el.type}
                  {el.type === 'TEXT' && el.field ? `: ${el.field}` : ''}
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
